import {PageFragment, PageFragmentProps, PageFragmentState} from "../shared/PageFragment";
import {PageWithSidebarContainer, PageWithSidebarContainerRenderer} from "../shared/PageWithSidebarContainer";
import React, {ReactElement, useState} from "react";
import {Sidebar, SidebarItem, SidebarItems, SidebarListener} from "../shared/Sidebar";
import {PathComponent} from "../index";
import {Button, Typography} from "@mui/material";
import {Outlet} from "react-router-dom";
import {StyledBoxColumn, StyledBoxRow, StyledContainer, StyledSpan} from "../shared/StyledComponents";
import {DIVIDER, PD_LG, PD_XLG} from "../shared/dimens";
import {FormGenContainer} from "../shared/FormGenContainer";
import {
  BaseSettings,
  GeneralSettings,
  PreferencesSettings,
  ProfileSettings,
  Settings,
  SocialsSettings
} from "./settings";
import {Action} from "../shared/types";
import {App} from "./App";
import {deleteAccount} from "./types";

function SettingsContent<T extends BaseSettings>(props: { settings: T }): ReactElement {
  const [saveEnabled, setSaveEnabled] = useState<boolean>(false);
  return <StyledBoxColumn>
    <FormGenContainer onContainerWillSave={() => setSaveEnabled(true)} content={props.settings}/>
    <StyledBoxRow style={{paddingTop: PD_LG, borderTop: DIVIDER}}>
      <StyledSpan/>
      <Button disabled={!saveEnabled} variant={"contained"} onClick={() => {
        setSaveEnabled(false);
        props.settings.save();
      }}>Save</Button>
    </StyledBoxRow>
  </StyledBoxColumn>;
}

abstract class BaseSettingsContentFragment<T extends BaseSettings> extends PageFragment {

  constructor(props: PageFragmentProps, context: any, readonly settings: T) {
    super(props, context);
  }

  renderContent(): React.ReactElement {
    return <StyledContainer>
      <StyledBoxColumn style={{padding: PD_XLG, gap: PD_XLG}}>
        <Typography>{this.getSettingsDescription()}</Typography>
        {this.renderSettingsContent()}
      </StyledBoxColumn>
    </StyledContainer>;
  }

  protected renderSettingsContent(): ReactElement {
    return <SettingsContent settings={this.settings}/>;
  }

  protected abstract getSettingsDescription(): string;
}

class GeneralSettingsContentFragment extends BaseSettingsContentFragment<GeneralSettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().general);
  }

  protected getTitle(): string | null | undefined {
    return "General settings";
  }

  protected getSettingsDescription(): string {
    return "Update your username & manage account";
  }

  protected renderSettingsContent(): React.ReactElement {
    return <StyledBoxColumn>
      {super.renderSettingsContent()}
    </StyledBoxColumn>;
  }
}

class ProfileSettingsContentFragment extends BaseSettingsContentFragment<ProfileSettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().profile);
  }

  protected getTitle(): string | null | undefined {
    return "Profile settings";
  }

  createToolbarActions(): Action[] {
    return [
      new Action("View profile", () => this.props.path.navigate("/profile")),
    ];
  }

  protected getSettingsDescription(): string {
    return "Set up your Showcase profile";
  }

  protected renderSettingsContent(): React.ReactElement {
    return <StyledBoxColumn>
      {super.renderSettingsContent()}
    </StyledBoxColumn>;
  }
}

class SocialsSettingsContentFragment extends BaseSettingsContentFragment<SocialsSettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().socials);
  }

  protected getTitle(): string | null | undefined {
    return "Socials settings";
  }

  protected getSettingsDescription(): string {
    return "Add social platform links to your profile";
  }
}

export class PreferencesFragment extends BaseSettingsContentFragment<PreferencesSettings> {

  constructor(props: PageFragmentProps, context: any) {
    super(props, context, Settings.getInstance().preferences);
  }

  protected getTitle(): string | null | undefined {
    return "Preferences";
  }

  protected getSettingsDescription(): string {
    return "Manage your Showcase preferences";
  }
}

const SIDEBAR_ITEMS: SidebarItems = {
  groups: [
    {
      items: [
        {
          title: "General",
          path: "general",
          render: pathProps => <GeneralSettingsContentFragment path={pathProps}/>,
        },
        {
          title: "Profile",
          path: "profile",
          render: pathProps => <ProfileSettingsContentFragment path={pathProps}/>,
        },
        {
          title: "Socials",
          path: "socials",
          render: pathProps => <SocialsSettingsContentFragment path={pathProps}/>,
        },
        {
          title: "Preferences",
          path: "preferences",
          render: pathProps => <PreferencesFragment path={pathProps}/>,
        },
      ],
      action: new Action("Delete Account", () => {
        App.CONTEXT.showTextDialog(
          "Confirm",
          "Are you sure you want to delete your account? This action cannot be undone, and you'll be logged out.",
          new Action("Delete Account", () => deleteAccount()).makeDestructive(),
          new Action("Cancel", () => App.CONTEXT.hideDialog()));
      }).makeDestructive(),
      title: "Settings",
      variant: "condensed",
    },
  ],
  containerId: "settings",
}

export type SettingsFragmentProps = PageFragmentProps & {}

type SettingsFragmentState = PageFragmentState & {
  selectedItem?: SidebarItem,
}

export class SettingsFragment extends PageFragment<SettingsFragmentProps, SettingsFragmentState> implements PageWithSidebarContainerRenderer, SidebarListener {

  static nestedPaths(): PathComponent[] {
    return PageWithSidebarContainer.nestedPathsFromSidebarItems(SIDEBAR_ITEMS);
  }

  renderContent(): ReactElement {
    return <PageWithSidebarContainer sidebarCollapsible renderer={this}/>;
  }

  renderPageWithSidebarContainerContent(): React.ReactElement {
    return <Outlet/>;
  }

  renderPageWithSidebarContainerSidebar(): React.ReactElement {
    return <Sidebar
      tabs={[{
        id: "default",
        items: SIDEBAR_ITEMS,
        selected: this.state.selectedItem,
        listener: this,
      }]}/>;
  }

  onSelectionChanged(item: SidebarItem) {
    this.setState({selectedItem: item});
  }
}